import numeral from 'numeral';
import ko from 'knockout';

export interface RateComponentSet {
    id: string;
    isDefault: boolean;
    name: string;
    currentSet: RateComponentSetDate;
    dates: RateComponentSetDate[];
    canBeDeleted: boolean;
}

export interface RateComponentSetDate {
    id: string;
    rateComponentSetId: string;
    startDate: string;
    minimumRates: RateComponentSetMinimumRate[];
    commissionRules: RateComponentSetCommissionRule[];
}

export interface RateComponentSetMinimumRate {
    id: string;
    rateComponentSetId: string;
    ageThreshold: number;
    rate: number;
    minimumRate: number;
}

export interface RateComponentSetCommissionRule {
    id: string;
    rateComponentSetId: string;
    ageThreshold: number;
    percentage: number;
}

export interface MinimumRateAndCommissionRuleCombined {
    rateComponentSetId: string;
    minimumRateId: string | null;
    commissionRuleId: string | null;
    ageThreshold: number;
    minimumRate: number | null;
    percentage: number | null;
}

export interface RequestHourlyRate {
    id: string;
    ageThreshold: number;
    rate: number;
    requestId: string;
}

export interface RequestCommissionRule {
    id: string;
    ageThreshold: number;
    percentage: number;
    requestId: string;
}

export interface RequestHourlyRateAndCommissionRuleCombined {
    requestHourlyRateId: string | null;
    requestCommissionRuleId: string | null;
    ageThreshold: number;
    rate: number | null;
    percentage: number | null;
}

export class EmployerRequestHourlyRateInputModel {
    parent: any;

    id: KnockoutObservable<string | null>;
    ageThreshold: KnockoutObservable<number>;
    minimumRate: KnockoutComputed<number>;
    rate: KnockoutObservable<number>;

    showInfoButton: KnockoutComputed<boolean>;
    showTooltip: KnockoutObservable<boolean>;

    constructor(parent: any, data: RateComponentSetMinimumRate) {
        this.parent = parent;

        this.id = ko.observable(data.id);
        this.ageThreshold = ko.observable(data.ageThreshold);

        this.minimumRate = ko.pureComputed(() => {
            const rateComponentSetDate = this.parent.rateComponentSetDate();
            const minimumRate = rateComponentSetDate?.minimumRates.find(
                (rate: any) => rate.ageThreshold == this.ageThreshold(),
            )?.minimumRate;

            return minimumRate ?? 0;
        });

        this.rate = ko
            .observable(
                data.rate != null
                    ? data.rate
                    : parent.companyHasOwnHourlyRate()
                      ? null
                      : data.minimumRate,
            )
            .extend({
                required: {
                    params: true,
                    message: 'Uurtarief is verplicht',
                },
                validation: [
                    {
                        validator: (val: string) => {
                            return numeral.validate(val, 'nl-nl');
                        },
                        message: 'Vul een geldig uurtarief in',
                    },
                    {
                        validator: (val: string) => {
                            const numberValue = numeral(val);

                            return numberValue.value() >= this.minimumRate();
                        },
                        message: () =>
                            `Uurtarief moet minstens €${numeral(this.minimumRate()).format('0,0.00')} per uur zijn`,
                    },
                ],
            });

        this.showTooltip = ko.observable(false);
        this.showInfoButton = ko.pureComputed(() => {
            return this.rate() != null && numeral(this.rate()).value() < 21.5;
        });
    }

    toggleTooltip() {
        this.showTooltip(!this.showTooltip());
    }
}

export class RequestCommissionRuleInputModel {
    id: KnockoutObservable<string | null>;
    ageThreshold: KnockoutObservable<number>;
    percentage: KnockoutObservable<number>;

    constructor(data: RateComponentSetCommissionRule) {
        this.id = ko.observable(data.id);
        this.ageThreshold = ko.observable(data.ageThreshold);
        this.percentage = ko.observable(data.percentage).extend({
            required: {
                params: true,
                message: 'Percentage is verplicht',
            },
            validation: [
                {
                    validator: (val: string) => {
                        return numeral.validate(val, 'nl-nl');
                    },
                    message: 'Vul een geldig percentage in',
                },
                {
                    validator: (val: string) => {
                        const numberValue = numeral(val);

                        return (
                            numberValue.value() >= 0 &&
                            numberValue.value() <= 100
                        );
                    },
                    message: () => `Percentage moet tussen 0% en 100% zijn`,
                },
            ],
        });
    }
}
